import { PageLayout } from "@/v2/components/layout";
import { CustomReportData } from "@/v2/entities/custom-report/CustomReport.model";
import { Flex, Text, Box, Heading, IconButton } from "@radix-ui/themes";
import { CheckIcon, Link2Icon } from "@radix-ui/react-icons";
import { useMemo, useCallback, useState } from "react";
import { CustomReportSection } from "./CustomReportSection";
import { Tooltip } from "@/components/shared/tooltip";
import styles from "./CustomReport.module.scss";

type CustomReportProps = CustomReportData & {
  tenantId: string;
  title: string;
  startDate: string;
  endDate: string;
};

function getSectionId(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function getDayWithOrdinal(day: number): string {
  if (day > 3 && day < 21) return `${day}th`;
  switch (day % 10) {
    case 1:
      return `${day}st`;
    case 2:
      return `${day}nd`;
    case 3:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
}

function formatDateRange(startDate: string, endDate: string): string {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const startMonth = start.toLocaleString("default", {
    month: "long",
    timeZone: "UTC",
  });
  const endMonth = end.toLocaleString("default", {
    month: "long",
    timeZone: "UTC",
  });
  const startDay = getDayWithOrdinal(start.getUTCDate());
  const endDay = getDayWithOrdinal(end.getUTCDate());
  return `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
}

export function CustomReport({
  tenantId,
  title,
  startDate,
  endDate,
  sections,
}: CustomReportProps) {
  const sectionIds = useMemo(
    () => sections.map((section) => getSectionId(section.title)),
    [sections]
  );

  const formattedDateRange = useMemo(
    () => formatDateRange(startDate, endDate),
    [startDate, endDate]
  );

  const [isCopied, setIsCopied] = useState(false);

  const handleCopyUrl = useCallback(() => {
    navigator.clipboard.writeText(window.location.href).then(
      () => {
        setIsCopied(true);
        setTimeout(() => {
          setIsCopied(false);
        }, 2000);
      },
      () => {
        // Handle error silently
      }
    );
  }, []);

  const handleAnchorClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
      e.preventDefault();
      const element = document.getElementById(id);
      if (element) {
        const url = new URL(window.location.href);
        url.hash = id;
        window.history.pushState({}, "", url.toString());
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
    []
  );

  return (
    <PageLayout variant="v2">
      <div className={styles.container}>
        <Box className={styles.headerSection}>
          <Flex align="center" gap="3">
            <Heading size="8" className={styles.pageTitle}>
              {tenantId}'s {title} Report
            </Heading>
            <Tooltip>
              <Tooltip.Trigger>
                <IconButton
                  variant="ghost"
                  size="2"
                  color={isCopied ? "green" : "gray"}
                  onClick={handleCopyUrl}
                  aria-label="Copy page URL"
                  style={{ marginTop: "4px" }}
                >
                  {isCopied ? <CheckIcon /> : <Link2Icon />}
                </IconButton>
              </Tooltip.Trigger>
              <Tooltip.Content>
                <Text size="2">
                  Anyone with this link can access the report
                </Text>
              </Tooltip.Content>
            </Tooltip>
          </Flex>
          <Text size="3" color="gray" className={styles.subtitle}>
            {formattedDateRange}
          </Text>
        </Box>
        <Flex gap="4" className={styles.contentWrapper}>
          <Box className={styles.anchorList}>
            <Text
              size="3"
              weight="bold"
              color="gray"
              highContrast
              className={styles.anchorListTitle}
            >
              Sections
            </Text>
            <Flex
              direction="column"
              gap="1"
              className={styles.anchorLinksContainer}
            >
              {sections.map((section, index) => {
                const id = sectionIds[index];
                return (
                  <a
                    key={index}
                    href={`#${id}`}
                    onClick={(e) => handleAnchorClick(e, id)}
                    className={styles.anchorLink}
                  >
                    <Text size="2" color="gray" style={{ lineHeight: 1.4 }}>
                      {section.title}
                    </Text>
                  </a>
                );
              })}
            </Flex>
          </Box>
          <Flex
            direction="column"
            gap="3"
            className={styles.contentArea}
            style={{ flex: 1 }}
          >
            {sections.map((section, index) => (
              <CustomReportSection key={index} data={section} />
            ))}
          </Flex>
        </Flex>
      </div>
    </PageLayout>
  );
}
